import React, { useState, useEffect, useMemo } from 'react';
import {
  Calendar, Clock, MapPin, User, Phone, Mail,
  Coffee, DollarSign, Star, ArrowLeft, Edit,
  CheckCircle, XCircle, AlertTriangle, RefreshCw, BookMarked, Printer
} from 'lucide-react';
// Mock data removed - using only real Supabase data
import { MeetingStorage } from '../utils/meetingStorage';
import { meetingService } from '../utils/supabaseData';
import CancellationDialog from '../components/CancellationDialog';
import { logger } from '../utils/logger';
import ProfilePicture from '../components/ProfilePicture';
import { savedOrdersService } from '../utils/savedOrdersService';
import SavedOrderPopup from '../components/SavedOrderPopup';
import SavedOrderIndicator from '../components/SavedOrderIndicator';
import PrintableAttendeeList from '../components/PrintableAttendeeList';
import { attendeeListService } from '../utils/attendeeListService';
import emailService from '../utils/emailService';
import { staffService } from '../utils/supabaseStaffService';
import { attendeeService } from '../services/attendeeService';

// Helper function to convert 24-hour to 12-hour format
const convertTo12Hour = (time24) => {
  if (!time24) return '';
  const [hours, minutes] = time24.split(':');
  const hour = parseInt(hours, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return `${displayHour}:${minutes} ${ampm}`;
};

const MeetingDetailView = ({ meetingId, setCurrentView, setSelectedMeetingId, currentUser, updateNotificationCount }) => {
  const [meeting, setMeeting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCancellationDialog, setShowCancellationDialog] = useState(false);
  const [actionInProgress, setActionInProgress] = useState(false);
  const [actionStatus, setActionStatus] = useState('');
  const [discussionNotes, setDiscussionNotes] = useState('');
  const [followUpNotes, setFollowUpNotes] = useState('');
  const [saveInProgress, setSaveInProgress] = useState(false);

  // Saved orders state
  const [savedOrders, setSavedOrders] = useState([]);
  const [showSavedOrderPopup, setShowSavedOrderPopup] = useState(false);
  const [editingSavedOrder, setEditingSavedOrder] = useState(null);

  // Print attendee list state
  const [attendeeListData, setAttendeeListData] = useState(null);
  const [loadingAttendeeList, setLoadingAttendeeList] = useState(false);

  // Attendees state
  const [attendees, setAttendees] = useState([]);
  const [loadingAttendees, setLoadingAttendees] = useState(false);
  const [userRsvpStatus, setUserRsvpStatus] = useState(null);
  const [updatingRsvp, setUpdatingRsvp] = useState(false);

  // Determine if user is the main customer or just an attendee
  // For office staff, check the physician they're acting on behalf of
  const effectiveUserId = currentUser?.delegatingFor?.id || currentUser?.id;
  const isAttendeeOnly = useMemo(() => {
    if (!meeting || !effectiveUserId) return false;
    // User is attendee-only if they're NOT the main customer_id
    return meeting.customer_id !== effectiveUserId;
  }, [meeting, effectiveUserId]);

  // Load meeting data from Supabase first, then fallback to localStorage/mock data
  useEffect(() => {
    const loadMeeting = async () => {
      try {
        logger.debug('🔍 Loading meeting detail for ID:', meetingId);

        // Try Supabase first - prioritize Supabase data completely
        let meetingData = null;
        let usingSupabase = false;

        try {
          // Pass currentUser.id to bypass RLS for customers/office staff
          meetingData = await meetingService.getMeetingById(meetingId, currentUser?.id);
          if (meetingData) {
            usingSupabase = true;
            logger.debug('✅ Loaded meeting from Supabase:', meetingData);
            logger.debug('🍽️ Restaurant fields from Supabase:', {
              restaurant_name: meetingData.restaurant_name,
              restaurant_address: meetingData.restaurant_address,
              restaurant_phone: meetingData.restaurant_phone,
              restaurant_cuisine_type: meetingData.restaurant_cuisine_type,
              restaurant_price_range: meetingData.restaurant_price_range,
              restaurant_rating: meetingData.restaurant_rating,
              restaurant_url: meetingData.restaurant_url
            });

            // Transform Supabase format to match expected UI format
            // Use spread operator to preserve ALL Supabase fields (including restaurant fields)
            const transformedMeeting = {
              ...meetingData, // Preserve all fields including restaurant_name, restaurant_address, etc.
              // Map Supabase fields to expected UI fields
              date: meetingData.meeting_date || meetingData.date,
              time: meetingData.start_time ? convertTo12Hour(meetingData.start_time) : meetingData.time,
              // Keep both formats for compatibility
              meeting_date: meetingData.meeting_date,
              start_time: meetingData.start_time
            };

            logger.debug('🔄 Transformed meeting for UI:', transformedMeeting);
            logger.debug('🍽️ Restaurant fields after transform:', {
              restaurant_name: transformedMeeting.restaurant_name,
              restaurant_address: transformedMeeting.restaurant_address,
              restaurant_phone: transformedMeeting.restaurant_phone
            });
            logger.debug('🚫 Cancellation fields from Supabase:', {
              cancelled_by: meetingData.cancelled_by,
              cancelled_by_name: meetingData.cancelled_by_name,
              cancelled_by_type: meetingData.cancelled_by_type,
              cancellation_reason: meetingData.cancellation_reason,
              status: meetingData.status
            });
            setMeeting(transformedMeeting);
            setDiscussionNotes(transformedMeeting.meeting_notes || transformedMeeting.notes || '');
            setFollowUpNotes(transformedMeeting.follow_up_actions || transformedMeeting.followUp || '');
          } else {
            throw new Error('Meeting not found in Supabase');
          }
        } catch (supabaseError) {
          logger.warn('⚠️ Supabase meeting detail load failed, trying localStorage fallback:', supabaseError);

          // Try MeetingStorage as fallback
          const storedMeeting = MeetingStorage.getMeetingById(meetingId);
          if (storedMeeting) {
            logger.debug('📱 Loaded meeting from localStorage:', storedMeeting);
            meetingData = storedMeeting;
            setDiscussionNotes(storedMeeting.notes || '');
            setFollowUpNotes(storedMeeting.followUp || '');
          } else {
            logger.warn('⚠️ Meeting not found in Supabase or localStorage');
            setMeeting(null);
            return;
          }
          setMeeting(meetingData);
        }

        logger.debug(`🔍 Using ${usingSupabase ? 'Supabase' : 'fallback'} data for meeting detail:`, meetingData);

      } catch (error) {
        logger.error('Error loading meeting:', error);
        logger.error('❌ Failed to load meeting:', error);
        setMeeting(null);
      } finally {
        setLoading(false);
      }
    };

    loadMeeting();
  }, [meetingId]);

  // Load attendees for this meeting and find current user's RSVP status
  useEffect(() => {
    const loadAttendees = async () => {
      if (!meetingId) return;

      setLoadingAttendees(true);
      try {
        const meetingAttendees = await attendeeService.getMeetingAttendees(meetingId);
        setAttendees(meetingAttendees || []);
        logger.debug('Loaded meeting attendees:', { count: meetingAttendees?.length });

        // Find current user's RSVP status if they're an attendee
        if (effectiveUserId && meetingAttendees) {
          const userAttendee = meetingAttendees.find(a => a.user_id === effectiveUserId);
          if (userAttendee) {
            setUserRsvpStatus(userAttendee.rsvp_status);
            logger.debug('User RSVP status:', userAttendee.rsvp_status);
          }
        }
      } catch (error) {
        logger.error('Error loading attendees:', error);
        setAttendees([]);
      } finally {
        setLoadingAttendees(false);
      }
    };

    loadAttendees();
  }, [meetingId, effectiveUserId]);

  // Load saved orders for this restaurant
  useEffect(() => {
    const loadSavedOrders = async () => {
      if (!meeting || !currentUser || currentUser.userType !== 'customer') {
        return;
      }

      const restaurantId = meeting.restaurant_yelp_id || meeting.restaurant_id || null;
      const restaurantName = meeting.restaurant_name || meeting.restaurant?.name;

      if (!restaurantName) {
        return;
      }

      try {
        // Use restaurant_yelp_id if available, otherwise fallback to restaurant name
        const searchKey = restaurantId || restaurantName;
        const orders = await savedOrdersService.getSavedOrdersByRestaurant(
          currentUser.id,
          searchKey
        );
        setSavedOrders(orders || []);
        logger.debug('Loaded saved orders for meeting restaurant', { count: orders?.length || 0 });
      } catch (error) {
        logger.error('Error loading saved orders for meeting', { error: error.message });
        setSavedOrders([]);
      }
    };

    loadSavedOrders();
  }, [meeting, currentUser]);

  // Build restaurant object from meeting data
  const restaurant = useMemo(() => {
    if (!meeting) {
      logger.debug('🍽️ No meeting data, restaurant is null');
      return null;
    }

    const restaurantObj = {
      name: meeting.restaurant_name || meeting.restaurant?.name || 'TBD',
      address: meeting.restaurant_address || meeting.restaurant?.address,
      phone: meeting.restaurant_phone || meeting.restaurant?.phone,
      cuisineType: meeting.restaurant_cuisine_type || meeting.restaurant?.cuisineType,
      priceRange: meeting.restaurant_price_range || meeting.restaurant?.priceRange,
      rating: meeting.restaurant_rating || meeting.restaurant?.rating,
      url: meeting.restaurant_url || meeting.restaurant?.url
    };

    logger.debug('🍽️ Built restaurant object:', restaurantObj);
    logger.debug('🍽️ meeting.restaurant_name =', meeting.restaurant_name);
    logger.debug('🍽️ restaurant.name =', restaurantObj.name);

    return restaurantObj;
  }, [meeting]);

  const salesRep = meeting ? (() => {
    if (meeting.sales_rep) {
      // Supabase format - transform to expected format
      return {
        ...meeting.sales_rep,
        name: `${meeting.sales_rep.first_name || ''} ${meeting.sales_rep.last_name || ''}`.trim(),
        email: meeting.sales_rep.email,
        phone: meeting.sales_rep.phone,
        company: meeting.sales_rep.company || 'Company Not Specified',
        territory: meeting.sales_rep.territory || 'Territory not specified',
        products: meeting.products || ['Product information not provided'] // Add products from meeting or default
      };
    }
    // Check for external sales rep (manually added meeting)
    if (meeting.external_sales_rep_name) {
      return {
        name: meeting.external_sales_rep_name,
        email: meeting.external_sales_rep_email || '',
        phone: meeting.external_sales_rep_phone || '',
        company: meeting.external_sales_rep_company || 'Company Not Specified',
        territory: 'Territory not specified',
        products: meeting.products || ['Product information not provided'],
        isExternal: true
      };
    }
    // For localStorage format, return basic info from meeting
    return {
      name: meeting.salesRep || 'Sales Representative',
      email: meeting.salesRepEmail || '',
      phone: meeting.salesRepPhone || '',
      company: meeting.salesRepCompany || 'Company Not Specified',
      territory: 'Territory not specified',
      products: meeting.products || ['Product information not provided']
    };
  })() : null;

  // Build customer/physician info from meeting data
  const customer = meeting ? (() => {
    if (meeting.customer) {
      // Supabase format - transform to expected format
      return {
        ...meeting.customer,
        name: `${meeting.customer.first_name || ''} ${meeting.customer.last_name || ''}`.trim(),
        email: meeting.customer.email,
        phone: meeting.customer.phone,
        specialty: meeting.customer.specialty || 'Specialty not specified',
        practice_name: meeting.customer.practice_name || meeting.customer.company || '',
        profile_picture: meeting.customer.profile_picture
      };
    }
    // For localStorage format, return basic info from meeting
    return {
      name: meeting.customer_name || meeting.customerName || 'Healthcare Provider',
      email: meeting.customer_email || '',
      phone: meeting.customer_phone || '',
      specialty: meeting.customer_specialty || 'Specialty not specified',
      practice_name: meeting.customer_practice_name || '',
      profile_picture: meeting.customer_profile_picture || null
    };
  })() : null;

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'text-green-600 bg-green-50';
      case 'pending': return 'text-yellow-600 bg-yellow-50';
      case 'cancelled': return 'text-red-600 bg-red-50';
      case 'completed': return 'text-blue-600 bg-blue-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'confirmed': return <CheckCircle className="w-4 h-4" />;
      case 'pending': return <Clock className="w-4 h-4" />;
      case 'cancelled': return <XCircle className="w-4 h-4" />;
      case 'completed': return <CheckCircle className="w-4 h-4" />;
      default: return <AlertTriangle className="w-4 h-4" />;
    }
  };

  const handleUpdateStatus = async (newStatus) => {
    setActionInProgress(true);
    try {
      logger.debug('📝 Updating meeting status:', { meetingId, newStatus });

      let updatedMeeting = null;

      // Try Supabase first
      try {
        const updateData = {
          status: newStatus,
          updated_at: new Date().toISOString()
        };

        // If confirming, add confirmation details
        if (newStatus === 'confirmed') {
          updateData.confirmed_by = currentUser.id;
          updateData.confirmed_at = new Date().toISOString();
        }

        updatedMeeting = await meetingService.updateMeeting(meetingId, updateData);

        if (updatedMeeting) {
          logger.debug('✅ Meeting status updated in Supabase');
          setMeeting({
            ...meeting,
            status: newStatus,
            confirmed_by: updateData.confirmed_by,
            confirmed_at: updateData.confirmed_at
          });
          setActionStatus(`Meeting ${newStatus} successfully!`);

          // Refresh notification count
          if (updateNotificationCount) {
            updateNotificationCount();
          }
        }
      } catch (supabaseError) {
        logger.warn('⚠️ Supabase update failed, trying localStorage fallback:', supabaseError);

        // Fallback to localStorage
        updatedMeeting = MeetingStorage.updateMeeting(meetingId, { status: newStatus });
        if (updatedMeeting) {
          logger.debug('✅ Meeting status updated in localStorage');
          setMeeting(updatedMeeting);
          setActionStatus(`Meeting ${newStatus} successfully!`);
        } else {
          throw new Error('Failed to update meeting');
        }
      }
    } catch (error) {
      logger.error('Error updating meeting status:', error);
      setActionStatus('Error updating meeting. Please try again.');
    } finally {
      setActionInProgress(false);
      setTimeout(() => setActionStatus(''), 3000);
    }
  };

  const handleCancelMeeting = async (cancellationData) => {
    try {
      logger.debug('🚫 Cancelling meeting:', meetingId, cancellationData);

      // Cancel meeting in Supabase using updateMeeting
      const updateData = {
        status: 'cancelled',
        cancelled_at: new Date().toISOString(),
        cancelled_by: cancellationData.cancelledBy,
        cancelled_by_name: cancellationData.cancelledByName,
        cancelled_by_type: cancellationData.cancelledByType,
        cancellation_reason: cancellationData.reason || '',
        cancellation_category: cancellationData.category || 'other',
        allow_reschedule: cancellationData.allowReschedule !== false,
        updated_at: new Date().toISOString()
      };

      logger.debug('📤 Updating meeting in Supabase:', updateData);

      const cancelledMeeting = await meetingService.updateMeeting(meetingId, updateData);

      if (cancelledMeeting) {
        logger.debug('✅ Meeting cancelled successfully:', cancelledMeeting);
        setMeeting({
          ...meeting,
          status: 'cancelled',
          cancelled_at: updateData.cancelled_at,
          cancelled_by: updateData.cancelled_by,
          cancelled_by_name: updateData.cancelled_by_name,
          cancelled_by_type: updateData.cancelled_by_type,
          cancellation_reason: updateData.cancellation_reason,
          cancellation_category: updateData.cancellation_category,
          allow_reschedule: updateData.allow_reschedule
        });
        setShowCancellationDialog(false);

        // Send email notification to sales rep
        try {
          logger.debug('📧 Preparing to send cancellation email to sales rep');

          // Format time for email (convert HH:MM:SS to HH:MM AM/PM)
          const formatTimeForEmail = (timeStr) => {
            if (!timeStr) return 'TBD';
            const timeParts = String(timeStr).split(':');
            const hours = parseInt(timeParts[0], 10);
            const minutes = parseInt(timeParts[1], 10);
            const ampm = hours >= 12 ? 'PM' : 'AM';
            const displayHour = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
            return `${displayHour}:${String(minutes).padStart(2, '0')} ${ampm}`;
          };

          // SAFE: Extract all values as primitive strings to prevent React error #31
          const physicianNameStr = String(
            (currentUser?.name && typeof currentUser.name === 'string') ? currentUser.name :
            (currentUser?.first_name && currentUser?.last_name) ? `${currentUser.first_name} ${currentUser.last_name}` :
            currentUser?.first_name || 'Healthcare Provider'
          );

          const salesRepNameStr = String(
            (salesRep?.name && typeof salesRep.name === 'string') ? salesRep.name :
            (salesRep?.first_name && salesRep?.last_name) ? `${salesRep.first_name} ${salesRep.last_name}` :
            salesRep?.first_name || meeting.sales_rep_name || 'Sales Representative'
          );

          const restaurantNameStr = String(
            (restaurant?.name && typeof restaurant.name === 'string') ? restaurant.name :
            meeting.restaurant_name || 'TBD'
          );

          const salesRepEmail = String(salesRep?.email || meeting.sales_rep_email || '');

          logger.debug('📧 Email parameters (all safe strings):', {
            physicianName: physicianNameStr,
            salesRepName: salesRepNameStr,
            restaurantName: restaurantNameStr,
            salesRepEmail
          });

          if (salesRepEmail) {
            const emailResult = await emailService.sendMeetingCancellationNotification({
              physicianName: salesRepNameStr,  // Recipient is sales rep
              physicianEmail: salesRepEmail,
              adminEmails: [],
              salesRepName: physicianNameStr,  // Person who cancelled is physician
              meetingDate: meeting.meeting_date || meeting.date,
              meetingTime: formatTimeForEmail(meeting.start_time || meeting.time),
              locationName: String(meeting.location_name || meeting.location || 'TBD'),
              restaurantName: restaurantNameStr,
              cancellationReason: String(cancellationData.reason || ''),
              cancellationCategory: String(cancellationData.category || 'other'),
              allowReschedule: cancellationData.allowReschedule !== false,
              suggestedDates: cancellationData.suggestedDates || []
            });

            if (emailResult.success) {
              logger.debug('✅ Cancellation email sent to sales rep');
              setActionStatus(`Meeting cancelled successfully. Notification sent to ${salesRepNameStr}.`);
            } else {
              logger.warn('⚠️ Failed to send cancellation email', emailResult);
              setActionStatus('Meeting cancelled successfully. Note: Email notification may not have been delivered.');
            }
          } else {
            logger.warn('⚠️ No sales rep email found');
            setActionStatus('Meeting cancelled successfully.');
          }
        } catch (emailError) {
          logger.error('❌ Error sending cancellation email:', emailError);
          setActionStatus('Meeting cancelled successfully. Note: Email notification could not be sent.');
        }

        setTimeout(() => setActionStatus(''), 8000);
      } else {
        throw new Error('Failed to cancel meeting');
      }
    } catch (error) {
      logger.error('❌ Error cancelling meeting:', error);
      setActionStatus(error.message || 'Error cancelling meeting. Please try again.');
      setTimeout(() => setActionStatus(''), 5000);
    }
  };

  const handleSaveNotes = async () => {
    setSaveInProgress(true);
    setActionStatus('');

    try {
      logger.debug('💾 Saving meeting notes:', {
        meetingId,
        discussionNotes,
        followUpNotes
      });

      // Try to save to Supabase first
      try {
        const updateData = {
          meeting_notes: discussionNotes,
          follow_up_actions: followUpNotes,
          updated_at: new Date().toISOString()
        };

        const updatedMeeting = await meetingService.updateMeeting(meetingId, updateData);

        if (updatedMeeting) {
          logger.debug('✅ Notes saved to Supabase successfully');
          setMeeting({
            ...meeting,
            notes: discussionNotes,
            followUp: followUpNotes,
            meeting_notes: discussionNotes,
            follow_up_actions: followUpNotes
          });
          setActionStatus('Notes saved successfully!');
          setTimeout(() => setActionStatus(''), 3000);
        }
      } catch (supabaseError) {
        logger.warn('⚠️ Supabase save failed, trying localStorage fallback:', supabaseError);

        // Fallback to localStorage
        const updatedMeeting = MeetingStorage.updateMeeting(meetingId, {
          notes: discussionNotes,
          followUp: followUpNotes
        });

        if (updatedMeeting) {
          logger.debug('✅ Notes saved to localStorage successfully');
          setMeeting(updatedMeeting);
          setActionStatus('Notes saved successfully!');
          setTimeout(() => setActionStatus(''), 3000);
        } else {
          throw new Error('Failed to save notes');
        }
      }
    } catch (error) {
      logger.error('❌ Error saving notes:', error);
      setActionStatus('Error saving notes. Please try again.');
      setTimeout(() => setActionStatus(''), 5000);
    } finally {
      setSaveInProgress(false);
    }
  };

  // Saved orders handlers
  const handleAddSavedOrder = () => {
    if (!restaurant || !currentUser) return;

    setEditingSavedOrder(null);
    setShowSavedOrderPopup(true);
  };

  const handleEditSavedOrder = (order) => {
    setEditingSavedOrder(order);
    setShowSavedOrderPopup(true);
  };

  const handleSaveSavedOrder = async (orderData) => {
    try {
      if (editingSavedOrder) {
        // Update existing order
        await savedOrdersService.updateSavedOrder(editingSavedOrder.id, orderData);
        setActionStatus('Saved order updated successfully!');
      } else {
        // Create new order
        const newOrder = await savedOrdersService.createSavedOrder({
          ...orderData,
          customer_id: currentUser.id,
          restaurant_yelp_id: meeting.restaurant_yelp_id || meeting.restaurant_id || null,
          restaurant_name: restaurant.name
        });
        setActionStatus('Saved order created successfully!');
      }

      // Reload saved orders
      const restaurantId = meeting.restaurant_yelp_id || meeting.restaurant_id || null;
      const searchKey = restaurantId || restaurant.name;
      const orders = await savedOrdersService.getSavedOrdersByRestaurant(
        currentUser.id,
        searchKey
      );
      setSavedOrders(orders || []);

      setShowSavedOrderPopup(false);
      setEditingSavedOrder(null);
      setTimeout(() => setActionStatus(''), 3000);
    } catch (error) {
      logger.error('Error saving order', { error: error.message });
      setActionStatus('Error saving order. Please try again.');
      setTimeout(() => setActionStatus(''), 5000);
    }
  };

  const handleDeleteSavedOrder = async (orderId) => {
    try {
      await savedOrdersService.deleteSavedOrder(orderId);
      setSavedOrders(savedOrders.filter(o => o.id !== orderId));
      setActionStatus('Saved order deleted successfully!');
      setTimeout(() => setActionStatus(''), 3000);
    } catch (error) {
      logger.error('Error deleting saved order', { error: error.message });
      setActionStatus('Error deleting saved order. Please try again.');
      setTimeout(() => setActionStatus(''), 5000);
    }
  };

  const handlePrintAttendeeList = async () => {
    try {
      setLoadingAttendeeList(true);
      logger.debug('Fetching attendee list for printing', { meetingId });

      const attendeeData = await attendeeListService.getAttendeeList(meetingId);
      setAttendeeListData(attendeeData);

      // Wait a moment for React to render the PrintableAttendeeList component
      setTimeout(() => {
        window.print();
        setLoadingAttendeeList(false);
      }, 100);
    } catch (error) {
      logger.error('Error loading attendee list for printing', { error: error.message });
      setActionStatus('Error loading attendee list. Please try again.');
      setLoadingAttendeeList(false);
      setTimeout(() => setActionStatus(''), 5000);
    }
  };

  // Handle RSVP update for attendee-only users
  const handleUpdateRsvp = async (newStatus) => {
    if (!effectiveUserId || !meetingId) return;

    setUpdatingRsvp(true);
    try {
      await attendeeService.updateRSVPByUser(effectiveUserId, meetingId, newStatus);
      setUserRsvpStatus(newStatus);
      setActionStatus(`RSVP updated to ${newStatus}`);
      setTimeout(() => setActionStatus(''), 3000);

      // Refresh attendees list to show updated status
      const meetingAttendees = await attendeeService.getMeetingAttendees(meetingId);
      setAttendees(meetingAttendees || []);
    } catch (error) {
      logger.error('Error updating RSVP:', error);
      setActionStatus('Error updating RSVP. Please try again.');
      setTimeout(() => setActionStatus(''), 5000);
    } finally {
      setUpdatingRsvp(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
          <span className="ml-3 text-gray-600">Loading meeting details...</span>
        </div>
      </div>
    );
  }

  if (!meeting) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-800 mb-2">Meeting Not Found</h3>
          <p className="text-gray-600 mb-4">The requested meeting could not be found.</p>
          <button
            onClick={() => setCurrentView(currentUser?.user_type === 'customer' || currentUser?.userType === 'customer' ? 'meetings' : 'history')}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
          >
            {currentUser?.user_type === 'customer' || currentUser?.userType === 'customer' ? 'Back to Calendar' : 'Back to Meeting History'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => setCurrentView(currentUser?.user_type === 'customer' || currentUser?.userType === 'customer' ? 'meetings' : 'history')}
          className="flex items-center text-gray-600 hover:text-gray-800 mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {currentUser?.user_type === 'customer' || currentUser?.userType === 'customer' ? 'Back to Calendar' : 'Back to Meeting History'}
        </button>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Meeting Details</h2>
            <p className="text-gray-600 mt-1">
              Meeting with {salesRep?.name || 'Sales Representative'}
            </p>
          </div>
          <div className={`px-3 py-1 rounded-full text-sm font-medium flex items-center space-x-1 ${getStatusColor(meeting.status)}`}>
            {getStatusIcon(meeting.status)}
            <span className="capitalize">{meeting.status}</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Meeting Information */}
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Meeting Information</h3>
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <Calendar className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="font-medium text-gray-700">Date</p>
                  <p className="text-gray-600">{meeting.date}</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <Clock className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="font-medium text-gray-700">Time</p>
                  <p className="text-gray-600">{meeting.time}</p>
                </div>
              </div>
              {/* Meeting Location (Practice Location) */}
              {meeting.location_name && (
                <div className="flex items-start space-x-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <MapPin className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div className="flex-1">
                    <p className="font-semibold text-blue-900 mb-1">📍 Meeting Location</p>
                    <p className="text-gray-800 font-semibold">{meeting.location_name}</p>
                    {meeting.location_address && (
                      <p className="text-sm text-gray-600 mt-1">{meeting.location_address}</p>
                    )}
                    <p className="text-xs text-blue-700 mt-2 italic">
                      Your meeting will be held here
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Sales Representative */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Sales Representative</h3>
            {salesRep ? (
              <div className="space-y-4">
                <div className="flex items-center space-x-4">
                  <ProfilePicture
                    src={meeting.sales_rep_profile_picture || meeting.sales_rep?.profile_picture}
                    name={salesRep.name}
                    size="lg"
                  />
                  <div>
                    <p className="font-semibold text-gray-800">{salesRep.name}</p>
                    <p className="text-gray-600">{salesRep.title}</p>
                    <p className="text-sm text-gray-500">{salesRep.company}</p>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center space-x-3">
                    <Phone className="w-4 h-4 text-gray-400" />
                    <a
                      href={`tel:${salesRep.phone?.replace(/\D/g, '')}`}
                      className="text-blue-600 hover:text-blue-800 hover:underline"
                    >
                      {salesRep.phone}
                    </a>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Mail className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-600">{salesRep.email}</span>
                  </div>
                </div>
                <div className="mt-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">Products</p>
                  <div className="flex flex-wrap gap-2">
                    {(salesRep?.products || []).map((product, index) => (
                      <span
                        key={index}
                        className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs"
                      >
                        {product}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-gray-500">Sales representative information not available</p>
            )}
          </div>

          {/* Healthcare Provider Section - For sales reps viewing a meeting */}
          {(currentUser?.userType === 'sales' || currentUser?.user_type === 'sales') && customer && (
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Healthcare Provider</h3>
              <div className="space-y-4">
                <div className="flex items-center space-x-4">
                  <ProfilePicture
                    src={meeting.customer_profile_picture || meeting.customer?.profile_picture || customer.profile_picture}
                    name={customer.name}
                    size="lg"
                  />
                  <div>
                    <p className="font-semibold text-gray-800">{customer.name}</p>
                    <p className="text-gray-600">{customer.specialty}</p>
                    {customer.practice_name && (
                      <p className="text-sm text-gray-500">{customer.practice_name}</p>
                    )}
                  </div>
                </div>
                <div className="space-y-2">
                  {customer.phone && (
                    <div className="flex items-center space-x-3">
                      <Phone className="w-4 h-4 text-gray-400" />
                      <a
                        href={`tel:${customer.phone?.replace(/\D/g, '')}`}
                        className="text-blue-600 hover:text-blue-800 hover:underline"
                      >
                        {customer.phone}
                      </a>
                    </div>
                  )}
                  {customer.email && (
                    <div className="flex items-center space-x-3">
                      <Mail className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-600">{customer.email}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Attendees Section */}
          {attendees.length > 0 && (
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center justify-between">
                <span>Meeting Attendees</span>
                <span className="text-sm font-normal text-gray-600">
                  {attendees.length} total
                </span>
              </h3>

              {loadingAttendees ? (
                <div className="text-center py-4">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="text-sm text-gray-600 mt-2">Loading attendees...</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {attendees.map((attendee) => {
                    const profile = attendee.user_profiles;
                    const name = profile ? `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || profile.email : 'Unknown';
                    const type = attendee.attendee_type;
                    const rsvp = attendee.rsvp_status;
                    const isOrganizer = attendee.is_organizer;

                    return (
                      <div
                        key={attendee.id}
                        className={`p-3 border rounded-lg ${
                          isOrganizer ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold text-sm ${
                              type === 'physician' ? 'bg-blue-500 text-white' :
                              type === 'sales_rep' ? 'bg-green-500 text-white' :
                              'bg-gray-500 text-white'
                            }`}>
                              {name.split(' ').map(n => n[0]).join('').toUpperCase() || '?'}
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">
                                {name}
                                {isOrganizer && (
                                  <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded">
                                    Organizer
                                  </span>
                                )}
                              </p>
                              <div className="flex items-center gap-2 mt-1">
                                <span className={`text-xs px-2 py-0.5 rounded ${
                                  type === 'physician' ? 'bg-blue-100 text-blue-700' :
                                  type === 'sales_rep' ? 'bg-green-100 text-green-700' :
                                  'bg-gray-100 text-gray-700'
                                }`}>
                                  {type === 'physician' ? 'Physician' :
                                   type === 'sales_rep' ? 'Sales Rep' :
                                   type === 'staff' ? 'Staff' : type}
                                </span>
                                {profile?.specialty && type === 'physician' && (
                                  <span className="text-xs text-gray-600">
                                    {profile.specialty}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div>
                            {rsvp === 'accepted' && (
                              <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded flex items-center gap-1">
                                <CheckCircle className="w-3 h-3" />
                                Accepted
                              </span>
                            )}
                            {rsvp === 'declined' && (
                              <span className="px-2 py-1 bg-red-100 text-red-800 text-xs font-medium rounded flex items-center gap-1">
                                <XCircle className="w-3 h-3" />
                                Declined
                              </span>
                            )}
                            {rsvp === 'pending' && !isOrganizer && (
                              <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-medium rounded flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                Pending
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Order Details and Actions */}
        <div className="space-y-6">
          {/* Restaurant (Food Delivery) */}
          <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-orange-500">
            <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
              <MapPin className="w-5 h-5 text-orange-600 mr-2" />
              Restaurant
            </h3>
            <div className="space-y-2">
              <div>
                <p className="font-semibold text-gray-800 text-lg">{restaurant?.name || 'TBD'}</p>
              </div>
              {restaurant?.address && (
                <div>
                  <p className="text-xs font-medium text-gray-700 mb-0.5">Address</p>
                  <a
                    href={`https://maps.apple.com/?q=${encodeURIComponent(restaurant.address)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
                  >
                    {restaurant.address} →
                  </a>
                </div>
              )}
              {restaurant?.phone && (
                <div>
                  <p className="text-xs font-medium text-gray-700 mb-0.5">Phone</p>
                  <a
                    href={`tel:${restaurant.phone.replace(/\D/g, '')}`}
                    className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
                  >
                    {restaurant.phone}
                  </a>
                </div>
              )}
              <div className="flex items-center gap-2 pt-1">
                {restaurant?.cuisineType && (
                  <span className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded">
                    {restaurant.cuisineType}
                  </span>
                )}
                {restaurant?.priceRange && (
                  <span className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded">
                    {restaurant.priceRange}
                  </span>
                )}
                {restaurant?.rating && (
                  <span className="text-xs px-2 py-1 bg-yellow-100 text-yellow-800 rounded">
                    ⭐ {restaurant.rating}
                  </span>
                )}
              </div>
              {restaurant?.url && (
                <div className="pt-1">
                  <a
                    href={restaurant.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-blue-600 hover:text-blue-800 hover:underline"
                  >
                    View on Yelp →
                  </a>
                </div>
              )}
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-2 mt-2">
                <p className="text-xs text-orange-700 italic">
                  Food will be ordered from here and delivered to meeting location
                </p>
              </div>
            </div>
          </div>

          {/* Saved Orders Section - Only for customers */}
          {restaurant && currentUser?.userType === 'customer' && (
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-800">Saved Orders</h3>
                <button
                  onClick={handleAddSavedOrder}
                  disabled={savedOrders.length >= 3}
                  className="flex items-center space-x-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors text-sm"
                  title={savedOrders.length >= 3 ? "Maximum 3 saved orders per restaurant" : "Add saved order"}
                >
                  <BookMarked className="w-4 h-4" />
                  <span>Add Order</span>
                </button>
              </div>

              {savedOrders.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <BookMarked className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                  <p className="text-sm">No saved orders for this restaurant yet.</p>
                  <p className="text-xs mt-1">Save your typical order for quick reference!</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {savedOrders.map((order) => (
                    <div key={order.id} className="border border-blue-200 bg-blue-50 rounded-lg p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          <BookMarked className="w-4 h-4 text-blue-600" />
                          <h4 className="font-semibold text-blue-900">{order.order_template_name}</h4>
                        </div>
                        {order.average_order_size && (
                          <span className="text-sm font-medium text-blue-700">
                            Avg: ${order.average_order_size}
                          </span>
                        )}
                      </div>

                      <div className="mb-3">
                        <p className="text-sm text-blue-800 whitespace-pre-wrap font-mono bg-white p-2 rounded border border-blue-200">
                          {order.order_items}
                        </p>
                      </div>

                      {order.order_notes && (
                        <div className="mb-3 text-xs text-blue-700 bg-blue-100 rounded p-2">
                          <span className="font-medium">Notes: </span>
                          {order.order_notes}
                        </div>
                      )}

                      {order.times_used > 0 && (
                        <p className="text-xs text-blue-600 mb-3">
                          Used {order.times_used} time{order.times_used !== 1 ? 's' : ''}
                          {order.last_used && ` • Last: ${new Date(order.last_used).toLocaleDateString()}`}
                        </p>
                      )}

                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleEditSavedOrder(order)}
                          className="flex-1 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-sm"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteSavedOrder(order.id)}
                          className="flex-1 px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors text-sm"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Order Summary */}
          {meeting.orderItems && (
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Order Details</h3>
              <div className="space-y-3">
                {meeting.orderItems.map((item, index) => (
                  <div key={index} className="flex items-start">
                    <div className="flex-1">
                      <p className="font-medium text-gray-800">
                        {item.quantity}x {item.name}
                      </p>
                      {item.modifications && (
                        <p className="text-sm text-gray-500">{item.modifications}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Meeting Notes - Only for main customer, not attendee-only users */}
          {!isAttendeeOnly && (
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Meeting Notes</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Discussion Topics
                  </label>
                  <textarea
                    value={discussionNotes}
                    onChange={(e) => setDiscussionNotes(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={4}
                    placeholder="Add notes about the meeting discussion..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Follow-up Actions
                  </label>
                  <textarea
                    value={followUpNotes}
                    onChange={(e) => setFollowUpNotes(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={3}
                    placeholder="Add any follow-up actions or next steps..."
                  />
                </div>
                <button
                  onClick={handleSaveNotes}
                  disabled={saveInProgress}
                  className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg flex items-center justify-center space-x-2 transition-colors"
                >
                  {saveInProgress ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      <span>Saving...</span>
                    </>
                  ) : (
                    <span>Save Notes</span>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Action Status */}
          {actionStatus && (
            <div className={`bg-white rounded-lg shadow-md p-4 border-l-4 ${
              actionStatus.includes('Error') || actionStatus.includes('Failed')
                ? 'border-red-500 bg-red-50'
                : 'border-green-500 bg-green-50'
            }`}>
              <div className="flex items-center space-x-2">
                {actionStatus.includes('Error') || actionStatus.includes('Failed') ? (
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                ) : (
                  <CheckCircle className="w-5 h-5 text-green-600" />
                )}
                <span className={`text-sm font-medium ${
                  actionStatus.includes('Error') || actionStatus.includes('Failed')
                    ? 'text-red-700'
                    : 'text-green-700'
                }`}>
                  {actionStatus}
                </span>
              </div>
            </div>
          )}

          {/* Cancellation Information */}
          {meeting.status === 'cancelled' && (
            <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-red-500">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                <XCircle className="w-5 h-5 text-red-600 mr-2" />
                Meeting Cancelled
              </h3>
              <div className="space-y-3">
                <div>
                  <p className="text-sm font-medium text-gray-700">Cancelled by:</p>
                  <p className="text-gray-600">
                    {meeting.cancelled_by_name || 'Unknown'}
                    {meeting.cancelled_at && (
                      <span className="text-sm text-gray-500 ml-2">
                        on {new Date(meeting.cancelled_at).toLocaleDateString()}
                      </span>
                    )}
                  </p>
                </div>
                {meeting.cancellation_reason && (
                  <div>
                    <p className="text-sm font-medium text-gray-700">Reason:</p>
                    <p className="text-gray-600">{meeting.cancellation_reason}</p>
                  </div>
                )}
                {meeting.cancellation_category && meeting.cancellation_category !== 'other' && (
                  <div>
                    <p className="text-sm font-medium text-gray-700">Category:</p>
                    <p className="text-gray-600 capitalize">{meeting.cancellation_category.replace('_', ' ')}</p>
                  </div>
                )}
                {meeting.allow_reschedule && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-4">
                    <p className="text-blue-800 text-sm">
                      <CheckCircle className="w-4 h-4 inline mr-1" />
                      Rescheduling is allowed for this meeting.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Meeting Actions */}
          {meeting.status !== 'cancelled' && (
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                {isAttendeeOnly ? 'Your RSVP' : 'Meeting Actions'}
              </h3>
              <div className="space-y-3">
                {/* Print Attendee List - available for all users */}
                <button
                  onClick={handlePrintAttendeeList}
                  disabled={loadingAttendeeList}
                  className="w-full bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg flex items-center justify-center space-x-2 transition-colors"
                >
                  {loadingAttendeeList ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      <span>Loading...</span>
                    </>
                  ) : (
                    <>
                      <Printer className="w-4 h-4" />
                      <span>Print Attendee List</span>
                    </>
                  )}
                </button>

                {/* RSVP Controls for attendee-only users */}
                {isAttendeeOnly && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span className="text-gray-700 font-medium">Your Status:</span>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        userRsvpStatus === 'accepted' ? 'bg-green-100 text-green-800' :
                        userRsvpStatus === 'declined' ? 'bg-red-100 text-red-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {userRsvpStatus === 'accepted' ? 'Attending' :
                         userRsvpStatus === 'declined' ? 'Not Attending' :
                         'Pending'}
                      </span>
                    </div>
                    <div className="flex space-x-3">
                      <button
                        onClick={() => handleUpdateRsvp('accepted')}
                        disabled={updatingRsvp || userRsvpStatus === 'accepted'}
                        className={`flex-1 px-4 py-2 rounded-lg flex items-center justify-center space-x-2 transition-colors ${
                          userRsvpStatus === 'accepted'
                            ? 'bg-green-100 text-green-800 cursor-default'
                            : 'bg-green-600 hover:bg-green-700 text-white'
                        }`}
                      >
                        {updatingRsvp ? (
                          <RefreshCw className="w-4 h-4 animate-spin" />
                        ) : (
                          <CheckCircle className="w-4 h-4" />
                        )}
                        <span>Accept</span>
                      </button>
                      <button
                        onClick={() => handleUpdateRsvp('declined')}
                        disabled={updatingRsvp || userRsvpStatus === 'declined'}
                        className={`flex-1 px-4 py-2 rounded-lg flex items-center justify-center space-x-2 transition-colors ${
                          userRsvpStatus === 'declined'
                            ? 'bg-red-100 text-red-800 cursor-default'
                            : 'bg-red-600 hover:bg-red-700 text-white'
                        }`}
                      >
                        {updatingRsvp ? (
                          <RefreshCw className="w-4 h-4 animate-spin" />
                        ) : (
                          <XCircle className="w-4 h-4" />
                        )}
                        <span>Decline</span>
                      </button>
                    </div>
                    <p className="text-sm text-gray-500 text-center">
                      You are invited to this meeting. Changing your RSVP will not cancel the meeting.
                    </p>
                  </div>
                )}

                {/* Main customer actions - Confirm/Cancel/Edit */}
                {!isAttendeeOnly && (
                  <div className="flex space-x-3">
                    {meeting.status === 'pending' && (
                      <>
                        <button
                          onClick={() => handleUpdateStatus('confirmed')}
                          disabled={actionInProgress}
                          className="flex-1 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg flex items-center justify-center space-x-2 transition-colors"
                        >
                          {actionInProgress ? (
                            <RefreshCw className="w-4 h-4 animate-spin" />
                          ) : (
                            <CheckCircle className="w-4 h-4" />
                          )}
                          <span>Confirm Meeting</span>
                        </button>
                        <button
                          onClick={() => setShowCancellationDialog(true)}
                          disabled={actionInProgress}
                          className="flex-1 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg flex items-center justify-center space-x-2 transition-colors"
                        >
                          <XCircle className="w-4 h-4" />
                          <span>Cancel Meeting</span>
                        </button>
                      </>
                    )}

                    {meeting.status === 'confirmed' && (
                      <button
                        onClick={() => setShowCancellationDialog(true)}
                        disabled={actionInProgress}
                        className="flex-1 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg flex items-center justify-center space-x-2 transition-colors"
                      >
                        <XCircle className="w-4 h-4" />
                        <span>Cancel Meeting</span>
                      </button>
                    )}

                    {/* Edit Meeting Button - available for all statuses except cancelled */}
                    <button
                      onClick={() => {
                        if (setSelectedMeetingId) {
                          setSelectedMeetingId(meetingId);
                        }
                        setCurrentView('edit-meeting');
                      }}
                      disabled={actionInProgress}
                      className="flex items-center justify-center space-x-2 px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg transition-colors"
                    >
                      <Edit className="w-4 h-4" />
                      <span>Edit Meeting</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Cancellation Dialog */}
      <CancellationDialog
        isOpen={showCancellationDialog}
        onClose={() => setShowCancellationDialog(false)}
        onConfirm={handleCancelMeeting}
        meeting={meeting}
        userType="customer"
        currentUser={currentUser}
      />

      {/* Saved Order Popup */}
      {showSavedOrderPopup && restaurant && (
        <SavedOrderPopup
          isOpen={showSavedOrderPopup}
          onClose={() => {
            setShowSavedOrderPopup(false);
            setEditingSavedOrder(null);
          }}
          restaurant={{
            name: restaurant.name,
            address: restaurant.address,
            yelp_business_id: meeting.restaurant_yelp_id || meeting.restaurant_id
          }}
          savedOrder={editingSavedOrder}
          customerId={currentUser?.id}
          onSave={handleSaveSavedOrder}
          onDelete={editingSavedOrder ? () => handleDeleteSavedOrder(editingSavedOrder.id) : null}
        />
      )}

      {/* Printable Attendee List (hidden on screen, visible when printing) */}
      {attendeeListData && (
        <PrintableAttendeeList attendeeData={attendeeListData} />
      )}
    </div>
  );
};

export default MeetingDetailView;